import { defineComponent, reactive, SetupContext, readonly, computed, ref, onMounted, toRefs, watch} from '@vue/composition-api';
import { OfficialDocumentType, OfficialDocumentFile } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import CommonCategory from '@/components/common/common-category.component.vue';             // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import vueFilePond from 'vue-filepond';
import vueRouter from '@/router/index';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import { FileOrigin, FilePondFile } from 'filepond'
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import RichTextEditor from '../common/rich-text-editor.component.vue';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                                   // Windowサイズ
    isAddMode: boolean;                                                                     // true: 登録処理, false: 編集処理
    organizationId: string;                                                                 // カテゴリ選択で選択した組織ID
    officialDocument: OfficialDocumentType;                                                 // 公文書フォーム
    numberPrefix: string;                                                                   // 発番接頭辞
    validateValues: ValidateType;                                                           // m_parametersテーブル
    displayCalendar: boolean;                                                               // カレンダー表示フラグ
    fileErrorMessage: string;                                                               // 添付ファイルのバリデーションメッセージ文字列
    isFileValid: boolean;                                                                   // 添付ファイルのバリデーション結果
    isBodyValid: boolean;                                                                   // 掲載内容のバリデーション結果
    isDarkTheme: boolean;                                                                   // ダークテーマ使用フラグ
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                                                  // パラメーター名
    value: string;                                                                          // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * 公文書登録/編集（管理）画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, FilePond, CommonCategory },
    setup(props, ctx: SetupContext) {
        const commonRef = ref<any>(null);                                                   // メッセージダイアログ
        const form = ref<any>(null);                                                        // 入力フォーム
        const categoryRef = ref<any>(null);                                                 // カテゴリー選択
        const router = vueRouter;                                                           // ルータ
        const pondRef = ref<any>(null);                                                     // FilePondへの参照
        const issueDivision = {                                                             // 発番ラジオボタンの値
            notEntered: SystemConst.ISSUE_DIVISION.NOT_ENABLED,
            select: SystemConst.ISSUE_DIVISION.SELECT,
            officeContact: SystemConst.ISSUE_DIVISION.OFFICE_CONTACT
        };
        const formHeight = SystemConst.FORM_HEIGHT.OFFICIAL_DOCUMENT;                       // フォーム高さ調整用変数

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                                     // ウィンドウサイズ
            isAddMode: true,                                                                // true: 登録処理, false: 編集処理
            organizationId: '',                                                             // カテゴリ選択で選択した組織ID
            officialDocument: {
                organizationId: '',
                numberPrefix: '',
                issueDivision: SystemConst.ISSUE_DIVISION.NOT_ENABLED,
                issueNo: '',
                title: '',
                issueDate: '',
                issueDateDisplay: '',
                files: [],
                body: '',
                bodyText: ''
            },                                                                              // 公文書フォーム
            numberPrefix: '',                                                               // 発番接頭辞
            validateValues: {},                                                             // m_parametersテーブル
            displayCalendar: false,                                                         // カレンダー表示フラグ
            fileErrorMessage: '',                                                           // 添付ファイルのバリデーションメッセージ文字列
            isFileValid: true,                                                              // 添付ファイルのバリデーション結果
            isBodyValid: true,                                                              // 掲載内容のバリデーション結果
            isDarkTheme: ctx.root.$vuetify.theme.dark                                       // ダークテーマ使用フラグ
        });
        let officialDocumentId = '';                                                        // 編集対象の公文書ID

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(ctx.root.$store.state.commonLayout, () => {
            state.isDarkTheme = ctx.root.$store.state.commonLayout.isDarkTheme;
        })

        // バリデーション用最大桁数
        const maxLength = {
            // 番号
            issueNo: SystemConst.OFFICIAL_DOCUMENT_MAX_LENGTH.ISSUE_NO,
            // タイトル
            title: SystemConst.OFFICIAL_DOCUMENT_MAX_LENGTH.TITLE,
            // 掲載内容
            body: SystemConst.OFFICIAL_DOCUMENT_MAX_LENGTH.BODY,
        };

        // 発番のバリデーション
        const issueDivisionRules = [
            // 必須チェック
            (v: any) => v !== issueDivision['notEntered'] || CommonFunction.replaceArray(MessageConst.E_UNSELECTED, ['発番'])
        ];

        // 番号のバリデーション
        const issueNoRules = [
            // 必須チェック
            (v: any) => state.officialDocument.issueDivision !== issueDivision['select'] || !!v ||
                CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['番号']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['issueNo'], '番号'),
            // 属性チェック
            (v: any) =>
                /^[0-9-]*$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['番号', '数字、ハイフン（-）のみ']),
            (v: any) =>
                /^[0-9]*$|^[0-9]+-[0-9]+$/.test(v) ||
                CommonFunction.replaceArray(MessageConst.E_REGEX, ['番号', '数字または（数字）-（数字）の形式'])
        ];

        // タイトルのバリデーション
        const titleRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['title'], 'タイトル'),
        ];

        // 発出日のバリデーション
        const issueDateRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['発出日']),
        ];

        // 掲載内容のバリデーション
        const bodyRules = [
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['body'], '掲載内容')
        ];

        // 添付ファイルのバリデーション
        const filesRules = [
            () => {
                // バリデーションはAPIの結果(m_parametersテーブル)を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // 添付ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = pondRef.value.getFiles();

                // バリデーション値
                const maxCount = Number(state.validateValues['fileCount'].value);
                const maxSize = Number(state.validateValues['fileSize'].value);
                const allowExtension = state.validateValues['fileExtension'].value;

                // ファイル数が1以上、上限以下であるか確認する
                if (files.length === 0) {
                    return MessageConst.E_FILE_REQUIRED;
                }
                if (files.length > maxCount) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_FILE_COUNT, [
                        "添付",
                        state.validateValues['fileCount'].value + 'ファイル以下']);
                }

                // ファイルサイズが0バイトより大きく、上限以下であるか確認する
                for (const key in files) {
                    if (files[key].fileSize > maxSize) {
                        // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_MAX_SIZE, [maxSize / 1024]);
                    } else if (files[key].fileSize === 0) {
                        return MessageConst.E_FILE_NO_DATA;
                    }
                }

                // 拡張子が登録対象か確認する
                for (const key in files) {
                    // 登録しようとするファイルの拡張子を得る
                    const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                    const fileExtension = parts[1];

                    // m_parametersの拡張子一覧
                    const validateExtension = allowExtension.split(',');

                    if (validateExtension.indexOf(fileExtension) === -1) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_EXTENSION, [allowExtension]);
                    }
                }

                // ファイル名が重複していないか確認する
                if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_FILE_NAME_DUPLICATE;
                }
                return true;
            }
        ];

        // カテゴリ選択のアクティブノードを監視する        
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // カテゴリが選択された場合
        watch(activeNode, (newVal, oldVal) => {
            // 初期表示でない場合
            if (oldVal.length !== 0) {
                // 公文書一覧（一般）画面へ遷移
                selectCategory();
            }
        })

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });
        async function init() {
            // カテゴリ取得
            await categoryRef.value.getCategory();
            state.organizationId = ctx.root.$store.state.officialDocument.activeNode[0];

            // 公文書登録初期表示取得APIで初期表示データを取得する
            // パラメータ：カテゴリ選択で選択した組織ID
            const response = await ctx.root.$store.dispatch('officialDocument/getInitParameters',
                {
                    organizationId: state.organizationId
                }
            );

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 公文書登録画面の場合、発番接頭辞を設定する
            // 公文書編集画面の場合は、後続の処理で発番接頭辞を設定する
            if (state.isAddMode) {
                state.numberPrefix = response.data.initialData.numberPrefix + " 第";
            }

            // バリデーション値を設定する
            state.validateValues = response.data.parameters;

            // 公文書編集画面の場合
            if (!state.isAddMode) {

                // 公文書情報取得APIで初期表示データを取得する
                // パラメータ：公文書ID
                const responseDocument = await ctx.root.$store.dispatch('officialDocument/getOperateDocument',
                    {
                        officialDocumentId: officialDocumentId
                    }
                );

                // 通信結果判別
                if (responseDocument.status !== HttpStatus.OK) {
                    if (responseDocument.status === HttpStatus.NOT_FOUND) {
                        // HTTPステータスコードが404の場合
                        // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
                        commonRef.value.displayErrorMessageTransitionDialog(responseDocument, '/official-documents/admin/list');
                        return;
                    }
                    // HTTPステータスコードが200, 404以外の場合
                    //errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(responseDocument);
                    return;
                }

                // 画面項目に初期表示データを設定する
                // 発番指定・事務連絡
                // 発番接頭辞
                // タイトル
                // 発出日
                // 掲載内容
                state.officialDocument = responseDocument.data.officialDocument;
                // 掲載内容が空の場合にbodyTextを初期化する
                if (!state.officialDocument.body) {
                    state.officialDocument.bodyText = '';
                }
                // 発番接頭辞を設定する
                state.numberPrefix = responseDocument.data.officialDocument.numberPrefix + " 第";
                // 発番が事務連絡の場合、番号なし
                if (state.officialDocument.issueDivision === issueDivision.officeContact) {
                    state.officialDocument.issueNo = '';
                }
                // 発出日(表示用)への変換を行う
                inputDate(state.officialDocument.issueDate)

                // 添付ファイルをfile-pondへ表示する
                responseDocument.data.officialDocument.files.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                    pondRef.value.addFile(e.fileName, {
                        type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                        file: {
                            name: e.fileName,       // ファイル名
                            size: e.fileSize        // ファイルサイズ
                        },
                        metadata: {
                            url: `/api/official-documents/${officialDocumentId}/${e.fileSequence}`   // ダウンロード時のURL
                        }
                    });
                });
            }
        }

        /**
         * 初期表示
         */
        created();
        async function created() {

            // 編集対象公文書のID取得
            const officialDocId = computed(() => ctx.root.$route.params.id);

            if (typeof officialDocId.value !== 'undefined') {
                // 編集対象の定数が格納されている場合
                // isAddModeをfalseに変更する
                state.isAddMode = false;

                // 公文書IDを保存
                officialDocumentId = officialDocId.value
            }

            if (state.isAddMode) {
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-plus-outline');

                // mainTitleを「公文書登録」に変更
                ctx.root.$store.commit('setMainTitle', '公文書登録');
            } else {
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-check-outline');

                // mainTitleを「公文書編集」に変更
                ctx.root.$store.commit('setMainTitle', '公文書編集');
            }
        }

        /**
         * 日付を表示用にフォーマット変換する
         * 
         * @param dateValue YYYY-MM-DD形式文字列
         */
        function inputDate(dateValue: string) {
            // YYYY-MM-DD ⇒ YYYY/MM/DD
            state.officialDocument.issueDateDisplay = dateValue.replaceAll('-', '/');
            state.displayCalendar = false;
        }

        /**
         * テキストボックスに表示されている日付をクリア
         */
        function clearDate() {
            // 表示をクリア(YYYY/MM/DD形式)
            state.officialDocument.issueDateDisplay = '';
            // 内部保持値をクリア
            state.officialDocument.issueDate = '';
        }

        /**
         * 登録確認ダイアログ表示
         */
        function displayDialog() {

            // バリデーションを行う
            //   ・フォームのバリデーション
            //   ・添付ファイルのバリデーション
            //   ・掲載内容（リッチテキスト）のバリデーション
            updateFiles();
            if (!form.value.validate() || !state.isFileValid || !state.isBodyValid) {
                // バリデーションエラーの際にフォームの一番上にスクロールする
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            let displayMessage = '';
            // 登録処理・編集処理の判定
            if (state.isAddMode) {
                // 登録処理の場合
                // 登録処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            } else {
                // 編集処理の場合
                // 編集処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['編集']);
            }

            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {

            let response: any;
            // 登録処理・編集処理の判断
            if (state.isAddMode) {
                // 登録処理の場合
                // 添付ファイルをFilePondから取得
                const files: File[]
                    = pondRef.value.getFiles().map((e: FilePondFile) => e.file);

                // 公文書登録APIでデータを登録する
                // POST /api/official-documents
                response = await ctx.root.$store.dispatch(
                    'officialDocument/addDocument',
                    {
                        officialDocument: {
                            'organizationId': state.organizationId,
                            'issueDivision': state.officialDocument.issueDivision,
                            'issueNo': state.officialDocument.issueNo,
                            'title': state.officialDocument.title,
                            'issueDate': state.officialDocument.issueDate,
                            'body': state.officialDocument.body,
                            'bodyText': state.officialDocument.bodyText,
                        },
                        files: files
                    }
                );
            } else {
                // 編集処理の場合
                const files: FilePondFile[] = pondRef.value.getFiles();

                // officialDocument.files（DB上のファイル）が、files（アップロードするファイル）
                // にないものはofficialDocument.filesから消す
                const newFiles: OfficialDocumentFile[] = [];
                state.officialDocument.files.forEach(e => {
                    if (files.filter(x => x.file.name === e.fileName).length !== 0) {
                        newFiles.push(e);
                    }
                });
                state.officialDocument.files = newFiles;

                // files（アップロードするファイル）をofficialDocument.filesへ追加する。
                // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
                // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
                files.forEach(e => {
                    if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                        if (state.officialDocument.files.filter(x => x.fileName === e.file.name).length !== 0) {
                            const r = state.officialDocument.files.find(x => x.fileName === e.file.name);
                            if (r) {
                                // ファイルサイズを更新・ファイルシーケンスを削除
                                r.fileSize = e.fileSize;
                                r.fileSequence = undefined;
                            }
                        } else {
                            // DBに無い、新しいファイル
                            state.officialDocument.files.push({
                                'fileName': e.file.name,
                                'fileSize': e.fileSize
                            });
                        }
                    }
                });

                // APIへ渡すためFilePondFileオブジェクトからFileオブジェクトを作る
                const realFiles: File[] = pondRef.value.getFiles().map((e: FilePondFile) => e.file);

                // 公文書編集APIでデータを編集する
                // POST /api/official-documents/{officialDocumentId}
                //  officialDocumentId : updateOfficialDocumentIdを設定
                response = await ctx.root.$store.dispatch(
                    'officialDocument/updateDocument',
                    {
                        officialDocumentId: officialDocumentId,
                        officialDocument: {
                            'organizationId': state.organizationId,
                            'issueDivision': state.officialDocument.issueDivision,
                            'issueNo': state.officialDocument.issueNo,
                            'title': state.officialDocument.title,
                            'issueDate': state.officialDocument.issueDate,
                            'body': state.officialDocument.body,
                            'bodyText': state.officialDocument.bodyText,
                            'files': state.officialDocument.files
                        },
                        files: realFiles
                    }
                );
            }

            // HTTPステータスコードが200以外の場合
            // エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            commonRef.value.displayMessageTransitionDialog(response, '/official-documents/admin/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            router.push('/official-documents/admin/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * 発番ラジオボタンの選択イベント
         */
        function divisionChange() {
            if (state.officialDocument.issueDivision === issueDivision['officeContact']) {
                // 事務連絡を選択した場合、番号を消去
                state.officialDocument.issueNo = '';
            }
        }

        /**
         * 添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateFiles() {

            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < filesRules.length; index++) {
                const rule: any = filesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.fileErrorMessage = valid;
                    state.isFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.fileErrorMessage = '';
                    state.isFileValid = true;
                }
            }
        }

        /**
         * リッチテキストエディタの入力イベント
         * 
         * @param body 掲載内容(HTML形式)
         * @param bodyText 掲載内容(文字のみ)
         */
        function textChange(body: string, bodyText: string) {

            // stateへ掲載内容文字列を設定する
            state.officialDocument.body = body;
            state.officialDocument.bodyText = bodyText;
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // 公文書一覧（一般）画面へ遷移
            router.push('/official-documents/list');
        }

        return {
            state: readonly(state),
            form,
            pondRef,
            commonRef,
            issueDivision,
            categoryRef,
            maxLength,
            issueDivisionRules,
            issueNoRules,
            titleRules,
            issueDateRules,
            bodyRules,
            filesRules,
            formHeight,
            divisionChange,
            inputDate,
            clearDate,
            displayDialog,
            processingData,
            textChange,
            updateFiles,
            back,
            onResize
        };
    }
});

